import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
    Bell,
    DollarSign,
    ExternalLink,
    FileText,
    Loader2,
    Send,
    Users,
} from "lucide-react";
import { toast } from "sonner";
import AiPageShell from "@/components/ai/AiPageShell";
import AraciKomutaDashboard from "@/components/araci/AraciKomutaDashboard";
import AraciKomutaAyarlar from "@/components/araci/AraciKomutaAyarlar";
import AraciLeadActions from "@/components/araci/AraciLeadActions";
import { araciMerkez } from "@/lib/api";
import AraciBildirimGonderPage from "./AraciBildirimGonderPage";

const PAGE_TABS = [
    { id: "komuta", label: "Komuta", icon: Users },
    { id: "leadler", label: "Leadler", icon: FileText },
    { id: "hakedis", label: "Hakediş", icon: DollarSign },
    { id: "bildirim", label: "Bildirimler", icon: Bell },
    { id: "gonder", label: "Bildirim Gönder", icon: Send },
];

const LEAD_DURUM = [
    { value: "", label: "Tümü" },
    { value: "BEKLEMEDE", label: "Beklemede" },
    { value: "INCELENDI", label: "İnceleniyor" },
    { value: "ONAYLANDI", label: "Onaylandı" },
    { value: "REDDEDILDI", label: "Reddedildi" },
];

function QuickLinkCard({ to, href, title, desc, icon: Icon }) {
    const inner = (
        <div className="rounded-xl border border-white/10 bg-[#0B0F19]/60 p-4 hover:border-[#D4AF37]/30 hover:bg-[#D4AF37]/5 transition group">
            <div className="flex items-start gap-3">
                {Icon && <Icon className="h-5 w-5 text-[#D4AF37] shrink-0 mt-0.5" />}
                <div>
                    <div className="text-sm font-semibold text-slate-100 group-hover:text-[#F3E5AB]">{title}</div>
                    <p className="text-xs text-slate-500 mt-1">{desc}</p>
                </div>
                {href && <ExternalLink className="h-3.5 w-3.5 text-slate-500 shrink-0" />}
            </div>
        </div>
    );
    if (href) {
        return (
            <a href={href} target="_blank" rel="noopener noreferrer" className="block">
                {inner}
            </a>
        );
    }
    return <Link to={to}>{inner}</Link>;
}

export default function AraciMerkezPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const tab = searchParams.get("tab") || "komuta";
    const [period, setPeriod] = useState({
        yil: new Date().getFullYear(),
        ay: 0,
    });
    const [leads, setLeads] = useState([]);
    const [leadFilter, setLeadFilter] = useState("");
    const [leadLoading, setLeadLoading] = useState(false);

    const setTab = (id) => {
        setSearchParams({ tab: id });
    };

    const loadLeads = useCallback(() => {
        setLeadLoading(true);
        araciMerkez
            .leads({ durum: leadFilter || undefined, limit: 120 })
            .then((d) => setLeads(d.items || []))
            .catch(() => {
                setLeads([]);
                toast.error("Lead listesi yüklenemedi.");
            })
            .finally(() => setLeadLoading(false));
    }, [leadFilter]);

    useEffect(() => {
        if (tab === "leadler") loadLeads();
    }, [tab, loadLeads]);

    return (
        <AiPageShell
            title="Aracılar Merkezi"
            subtitle="Komuta merkezi, lead kuyruğu, hakediş ve bildirim yönetimi"
            icon={Users}
        >
            <div className="flex flex-wrap gap-1 mb-6 border-b border-white/10 pb-2">
                {PAGE_TABS.map((t) => {
                    const Icon = t.icon;
                    const active = tab === t.id;
                    return (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => setTab(t.id)}
                            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition ${
                                active
                                    ? "bg-[#D4AF37]/20 text-[#F3E5AB] border border-[#D4AF37]/40"
                                    : "text-slate-400 hover:text-slate-200 hover:bg-white/5"
                            }`}
                        >
                            {Icon && <Icon className="h-3.5 w-3.5" />}
                            {t.label}
                        </button>
                    );
                })}
            </div>

            {tab === "komuta" && (
                <div className="space-y-5">
                    <AraciKomutaAyarlar />
                    <AraciKomutaDashboard period={period} onPeriodChange={setPeriod} />
                </div>
            )}

            {tab === "leadler" && (
                <section className="rounded-xl border border-amber-500/20 bg-[#0B0F19]/50 p-4 space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <h2 className="text-sm font-semibold text-slate-100 flex items-center gap-2">
                            <FileText className="h-4 w-4 text-amber-400" />
                            Dosya önerileri (Lead) kuyruğu
                        </h2>
                        <div className="flex items-center gap-2">
                            <select
                                value={leadFilter}
                                onChange={(e) => setLeadFilter(e.target.value)}
                                className="h-8 rounded-lg bg-[#0B0F19] border border-white/10 px-2 text-xs text-slate-200"
                            >
                                {LEAD_DURUM.map((d) => (
                                    <option key={d.value} value={d.value}>{d.label}</option>
                                ))}
                            </select>
                            <button
                                type="button"
                                onClick={loadLeads}
                                disabled={leadLoading}
                                className="h-8 px-3 rounded-lg border border-white/10 text-xs text-slate-300 hover:bg-white/5 disabled:opacity-50"
                            >
                                Yenile
                            </button>
                        </div>
                    </div>
                    {leadLoading ? (
                        <div className="py-8 text-center text-slate-500">
                            <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                        </div>
                    ) : (
                        <div className="overflow-x-auto rounded-lg border border-white/10">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-[10px] uppercase text-slate-500 bg-black/20">
                                        <th className="py-2 px-3">Ad</th>
                                        <th className="py-2 px-3">Telefon</th>
                                        <th className="py-2 px-3">Plaka</th>
                                        <th className="py-2 px-3">Aracı</th>
                                        <th className="py-2 px-3">Durum</th>
                                        <th className="py-2 px-3">Tarih</th>
                                        <th className="py-2 px-3 text-right">İşlem</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {leads.map((l) => (
                                        <tr key={l.id} className="border-t border-white/5 hover:bg-white/5">
                                            <td className="py-2 px-3 text-slate-200">{l.ad_soyad}</td>
                                            <td className="py-2 px-3 font-mono text-xs text-slate-400">{l.telefon}</td>
                                            <td className="py-2 px-3 font-mono text-xs">{l.plaka || "—"}</td>
                                            <td className="py-2 px-3 text-xs">
                                                <Link to={`/e/aracilar/${l.araci_id}`} className="text-sky-400 hover:underline">
                                                    {l.araci_ad}
                                                </Link>
                                            </td>
                                            <td className="py-2 px-3">
                                                <span
                                                    className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                                        l.durum === "ONAYLANDI"
                                                            ? "bg-emerald-500/10 text-emerald-400"
                                                            : l.durum === "REDDEDILDI"
                                                              ? "bg-rose-500/10 text-rose-400"
                                                              : "bg-amber-500/10 text-amber-400"
                                                    }`}
                                                >
                                                    {l.durum_label}
                                                    {l.bekleme_gun >= 3 && l.durum === "BEKLEMEDE" && (
                                                        <span className="text-rose-400"> · {l.bekleme_gun}g</span>
                                                    )}
                                                </span>
                                            </td>
                                            <td className="py-2 px-3 text-xs text-slate-500">{l.olusturma_tarihi}</td>
                                            <td className="py-2 px-3 text-right">
                                                <AraciLeadActions lead={l} onDone={loadLeads} />
                                            </td>
                                        </tr>
                                    ))}
                                    {leads.length === 0 && (
                                        <tr>
                                            <td colSpan={7} className="py-8 text-center text-slate-500 text-xs">
                                                Kayıt bulunamadı.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <p className="text-[10px] text-slate-500">
                        Onay: müvekkil oluşturulur veya telefon eşleşmesi kullanılır. Aracıya panel + e-posta bildirimi gider.
                    </p>
                </section>
            )}

            {tab === "hakedis" && (
                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <QuickLinkCard
                            to="/e/araci-odemeleri"
                            title="Aracı ödemeleri"
                            desc="Dosya bazlı ödeme kayıtları — bekleyen ve ödenen"
                            icon={DollarSign}
                        />
                        <QuickLinkCard
                            to="/e/aracilar"
                            title="Aracı listesi"
                            desc="Müvekkil hakediş tutarları aracı detayında"
                            icon={Users}
                        />
                    </div>
                    <p className="text-xs text-slate-500 rounded-lg border border-white/10 bg-[#0B0F19]/40 px-4 py-3">
                        Hakediş tek satır modeli (AraciHakedis) aktif; müvekkil ve ödeme kayıtlarıyla senkron tutulur.
                        Komuta sekmesinden bekleyen satırları &quot;Ödendi&quot; işaretleyebilirsiniz.
                    </p>
                </div>
            )}

            {tab === "bildirim" && (
                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <QuickLinkCard
                            to="/yeni/araci-bildirim-gonder"
                            title="Bildirim gönder"
                            desc="Şablonlu toplu panel bildirimi"
                            icon={Send}
                        />
                        <QuickLinkCard
                            to="/e/araci-bildirimler"
                            title="Bildirim geçmişi"
                            desc="Tüm aracı bildirim kayıtları"
                            icon={Bell}
                        />
                    </div>
                </div>
            )}
        
            {tab === "gonder" && (
                <div className="rounded-xl border border-white/10 bg-[#0B0F19]/40 overflow-hidden">
                    <AraciBildirimGonderPage />
                </div>
            )}
        </AiPageShell>
    );
}
